import axios from 'axios'

import { BASE_URL } from '@/constants/api'
import { redirectOnUnauthorized } from '@/lib/axios'
import type { AuthAdminUpdateMe, IAdminTokens, ICustomerUser } from '@/types'

/**
 * Bare axios client dedicated to the admin auth endpoints.
 *
 * It deliberately does NOT reuse the shared `axiosPlugin`: login / refresh / forgot / reset are
 * unauthenticated calls that must never trigger the Authorization request interceptor (which would
 * recurse back into `checkAndUpdateAuthToken` → refresh). For the authenticated calls (`me`,
 * `logout`) the bearer token is passed explicitly. It still shares the same 401 response handling,
 * so a revoked/expired session on any of those authenticated calls (e.g. `me()` from
 * `refreshUser()`) logs the admin out the same way a 401 on `axiosPlugin` would.
 */
const authClient = axios.create({ baseURL: BASE_URL })
authClient.interceptors.response.use((response) => response, redirectOnUnauthorized)

// All HTTP responses are wrapped by the API's ResponseInterceptor as `{ status, data, ... }`.
function unwrap<T>(response: { data: { data: T } }): T {
  return response.data.data
}

export const AdminAuthService = {
  login(email: string, password: string): Promise<IAdminTokens> {
    return authClient.post('/auth/admin/login', { email, password }).then(unwrap<IAdminTokens>)
  },

  /** Exchanges the single-use SSO apikey (from the partner exchange) for an admin session. */
  sso(apikey: string): Promise<IAdminTokens> {
    return authClient.post('/auth/sso', { apikey }).then(unwrap<IAdminTokens>)
  },

  refresh(refreshToken: string): Promise<IAdminTokens> {
    return authClient.post('/auth/admin/refresh', { refreshToken }).then(unwrap<IAdminTokens>)
  },

  async logout(accessToken: string, refreshToken?: string): Promise<void> {
    await authClient.post(
      '/auth/admin/logout',
      { refreshToken },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    )
  },

  me(accessToken: string): Promise<ICustomerUser> {
    return authClient
      .get('/auth/admin/me', { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(unwrap<ICustomerUser>)
  },

  updateMe(accessToken: string, data: AuthAdminUpdateMe): Promise<ICustomerUser> {
    return authClient
      .patch('/auth/admin/me', data, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(unwrap<ICustomerUser>)
  },

  /** Makes `id_golfclub` the active club and returns a token pair carrying the new custom:gcs. */
  switchClub(accessToken: string, id_golfclub: number): Promise<IAdminTokens> {
    return authClient
      .post(
        '/auth/admin/switch-club',
        { id_golfclub },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      )
      .then(unwrap<IAdminTokens>)
  },

  async forgotPassword(email: string): Promise<void> {
    await authClient.post('/auth/admin/forgot-password', { email })
  },

  async resetPassword(email: string, token: string, password: string): Promise<void> {
    await authClient.post('/auth/admin/reset-password', { email, token, password })
  },
}
